import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { categoryApi } from '../services/api';

const CategoryContext = createContext(null);

export const useCategories = () => {
  const context = useContext(CategoryContext);
  if (!context) {
    throw new Error('useCategories must be used within a CategoryProvider');
  }
  return context;
};

// Only what may be booked on: archived categories and archived subcategories
// stay out of every selection field, while `categories` keeps them so old
// expenses can still resolve their name.
const selectable = (categories) =>
  categories
    .filter((category) => category.is_active)
    .map((category) => ({
      ...category,
      subcategories: (category.subcategories || []).filter((s) => s.is_active),
    }));

const buildFlatOptions = (categories) => {
  const options = [];
  categories.forEach((category) => {
    options.push({
      key: `c-${category.id}`,
      categoryCode: category.code,
      categoryName: category.name,
      categoryIcon: category.icon || null,
      subcategoryId: null,
      subcategoryName: null,
      subcategoryIcon: null,
      label: category.name,
    });
    (category.subcategories || []).forEach((subcategory) => {
      options.push({
        key: `s-${subcategory.id}`,
        categoryCode: category.code,
        categoryName: category.name,
        categoryIcon: category.icon || null,
        subcategoryId: subcategory.id,
        subcategoryName: subcategory.name,
        subcategoryIcon: subcategory.icon || null,
        // The label carries the subcategory's own emoji; the category icon is
        // rendered separately by the picker so it can sit in front of both.
        label: `${category.name} → ${subcategory.icon ? `${subcategory.icon} ` : ''}${subcategory.name}`,
      });
    });
  });
  return options;
};

/**
 * One provider for the whole app. It always fetches archived entries too and
 * lets consumers pick what they need:
 *
 *   categories       everything, for management and for resolving old codes
 *   activeCategories selectable only, archived subcategories stripped
 *   flatOptions      the picker's search list, built from activeCategories
 *
 * Deliberately not parameterised per consumer: a second provider around the
 * management view meant its refresh() never reached the picker, so a newly
 * created category was invisible until a full page reload.
 */
export const CategoryProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await categoryApi.list(true);
      setCategories(data);
      setError(null);
    } catch (err) {
      console.error('Failed to load categories:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const byCode = useMemo(() => {
    const map = {};
    categories.forEach((category) => {
      map[category.code] = category;
    });
    return map;
  }, [categories]);

  const activeCategories = useMemo(() => selectable(categories), [categories]);

  const flatOptions = useMemo(() => buildFlatOptions(activeCategories), [activeCategories]);

  const value = useMemo(
    () => ({ categories, activeCategories, byCode, flatOptions, loading, error, refresh }),
    [categories, activeCategories, byCode, flatOptions, loading, error, refresh]
  );

  return <CategoryContext.Provider value={value}>{children}</CategoryContext.Provider>;
};
